import * as Sentry from "@sentry/react"

import { errorReportingContext, shouldReportError } from "@/lib/errors/reporting"
import { getSessionTraceId } from "./request-context"
import { AppError, getRequestIdFromError } from "./app-error"

const MOCK_MODE = import.meta.env.VITE_USE_MOCK_DATA === "true"

export function initObservability(): void {
  if (MOCK_MODE) return

  const dsn = import.meta.env.VITE_SENTRY_DSN
  if (dsn) {
    Sentry.init({
      dsn,
      environment: import.meta.env.MODE,
      release: import.meta.env.VITE_APP_VERSION,
      integrations: [Sentry.browserTracingIntegration()],
      tracesSampleRate: 0.1,
      beforeSend(event) {
        if (event.request?.headers) {
          delete event.request.headers.Authorization
          delete event.request.headers.apikey
        }
        return event
      },
    })
    Sentry.setTag("sessionTraceId", getSessionTraceId())
  }

  window.addEventListener("unhandledrejection", (event) => {
    reportError(event.reason, { source: "unhandledrejection" })
  })

  window.addEventListener("error", (event) => {
    reportError(event.error ?? event.message, { source: "window.error" })
  })
}

export function reportError(error: unknown, context?: Record<string, unknown>): void {
  if (MOCK_MODE) return
  if (!shouldReportError(error)) return

  const requestId = getRequestIdFromError(error)
  const reportingContext = errorReportingContext(error, context)
  const dsn = import.meta.env.VITE_SENTRY_DSN

  if (dsn) {
    Sentry.withScope((scope) => {
      if (requestId) scope.setTag("requestId", requestId)
      scope.setTag("sessionTraceId", getSessionTraceId())
      if (reportingContext.errorCode) scope.setTag("errorCode", String(reportingContext.errorCode))
      scope.setExtras(reportingContext)
      Sentry.captureException(error)
    })
    return
  }

  if (import.meta.env.DEV) {
    console.error("[observability]", error, reportingContext)
  }
}

export function reportMessage(message: string, context?: Record<string, unknown>): void {
  if (MOCK_MODE) return

  const dsn = import.meta.env.VITE_SENTRY_DSN
  if (dsn) {
    Sentry.withScope((scope) => {
      scope.setTag("sessionTraceId", getSessionTraceId())
      if (context) scope.setExtras(context)
      Sentry.captureMessage(message)
    })
    return
  }

  if (import.meta.env.DEV) {
    console.warn("[observability]", message, context)
  }
}

export function toAppError(
  userMessage: string,
  options?: { requestId?: string; code?: string; cause?: unknown; reportable?: boolean },
): AppError {
  return new AppError(userMessage, options)
}

export { Sentry }
